import React, { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import useNurses from "@hooks/useNurses";
import { useTeamsQuery } from "@/hooks/roster-craete/useTeamsQuery";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import TeamSettingContents, {
  type TeamSettingContentsHandle,
} from "./TeamSettingContents";
import { ManagementDialog } from "@/components/modal/ManagementDialog";
import { ConfirmationDialog } from "@/components/modal/ConfirmationDialog";
import { Button } from "@/components/ui/button";
import type { GroupMemberInMonth } from "@/api/nurses";
import type { NurseResponse } from "@vTypes/nurse-type";

interface TeamSettingModalProps {
  onClose: () => void;
  selectedGroupId?: string | null;
  selectedYear: number;
  selectedMonth: number;
  memberStatusMap?: Map<string, GroupMemberInMonth>;
  nurseDatas?: NurseResponse[];
}

const TeamSettingModal: React.FC<TeamSettingModalProps> = ({
  onClose,
  selectedGroupId,
  selectedYear,
  selectedMonth,
  memberStatusMap,
  nurseDatas,
}) => {
  const gid = selectedGroupId ?? "";
  const contentsRef = useRef<TeamSettingContentsHandle>(null);
  const [savePending, setSavePending] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);
  const { isLoading: teamsLoading } = useTeamsQuery(gid);
  const { isLoading: isShiftsLoading } = useShiftsQuery(gid);
  const hasInjectedNurses = Array.isArray(nurseDatas);
  const { data: fallbackNurseDatas, isLoading: nurseLoading } = useNurses(gid, {
    enabled: !hasInjectedNurses,
  });
  const effectiveNurseDatas = nurseDatas ?? fallbackNurseDatas;
  const isLoading = teamsLoading || isShiftsLoading || nurseLoading;

  const requestClose = () => {
    if (savePending) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setIsExitConfirmOpen(true);
  };

  const closeWithoutSave = () => {
    setIsExitConfirmOpen(false);
    onClose();
  };

  const saveAndClose = () => {
    contentsRef.current?.triggerSaveDirect();
  };

  return (
    <>
      <ConfirmationDialog
        open={isExitConfirmOpen}
        onClose={closeWithoutSave}
        onConfirm={saveAndClose}
        isPending={savePending}
        pendingText="저장 중..."
        heading="팀 설정을 닫으시겠습니까?"
        title="변경사항을 저장하거나 저장하지 않고 닫을 수 있습니다."
        confirmText="저장"
        cancelText="저장하지 않고 닫기"
      />
      <ManagementDialog
        onClose={requestClose}
        title="Team 설정"
        widthClassName="max-w-[1500px]"
        bodyClassName="px-0"
        isBusy={savePending}
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              disabled={savePending}
              onClick={requestClose}
            >
              닫기
            </Button>
            <Button
              type="button"
              className="rounded-full bg-sig text-white hover:bg-sig/90"
              disabled={savePending || isLoading}
              onClick={() => contentsRef.current?.triggerSave()}
            >
              {savePending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : null}
              {savePending ? "저장 중..." : "저장"}
            </Button>
          </>
        }
      >
        {isLoading ? (
          <div
            role="status"
            className="flex min-h-60 items-center justify-center gap-2 text-sm text-muted-foreground"
          >
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            팀 설정을 불러오는 중입니다.
          </div>
        ) : null}
        <TeamSettingContents
          ref={contentsRef}
          onClose={onClose}
          selectedGroupId={gid}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          memberStatusMap={memberStatusMap}
          nurseDatas={effectiveNurseDatas}
          onMutationPendingChange={setSavePending}
          onDirtyChange={setIsDirty}
          hideTitle
        />
      </ManagementDialog>
    </>
  );
};

export default TeamSettingModal;
